import BaseSearchController, { PIN_COLORS } from "./base_search_controller"
import { MarkerClusterer, Cluster, AbstractAlgorithm } from "@googlemaps/markerclusterer";
const PRODUCT_PER_PAGE = 20

// Custom clustering algorithm that groups markers with the exact same location
class ExactLocationAlgorithm extends AbstractAlgorithm   {
  calculate({ markers }) {
    if (!markers || markers.length === 0) {
      return { clusters: [] };
    }

    const groups = new Map();

    markers.forEach((marker) => {
      const pos = marker.position;
      if (!pos) return;

      const key = `${pos.lat},${pos.lng}`;

      if (!groups.has(key)) { groups.set(key, []); }
      groups.get(key).push(marker);
    });

    const clusters = Array.from(groups.values()).map((clusterMarkers) => {
      return new Cluster({
        markers: clusterMarkers
      });
    });

    return { clusters: clusters };
  }
}

export default class extends BaseSearchController {
  static targets = [
    "centerPoint",
    "form",
    "map",
    "productInfoWindow",
    "productList",
    "productListItem",
    "results",
    "queryInfo",
    "noResultsMessage",
    "modalBody",
    "mobileFilterButton",
  ]

  async resultsTargetConnected() {
    this.scrollResultsToTop()
  }

  addMarker(productInfoWindow) {
    super.addMarker(productInfoWindow);

    if (this.markers.length == this.productInfoWindowTargets.length) {
      this.initMarkerClusterer();
    }
  }

  addMapListeners() {
    super.addMapListeners()

    google.maps.event.addListener(this.map, "idle", () => {
      if (this.mapMovedByUser && !this.boundsFitted) {
        this.resetFormElementAddress()
        this.searchFromMap()
        this.mapMovedByUser = false

        if (!this.hasTrackedMapMoved) {
          analytics.track("Activity Search Map Moved")
          this.hasTrackedMapMoved = true
        }
      } else {
        this.boundsFitted = false
      }
    })
  }

  // Add an infoWindow to a marker that displays a productInfoWindow
  // Close the infoWindow if another marker is clicked
  addInfoWindow(marker, productInfoWindow) {
    marker.addListener("click", () => {
      const { activityId, activityBoost } = productInfoWindow.dataset
      if (this.trackedMapMarkerPinsList.indexOf(activityId) == -1) {
        analytics.track("Activity Search Map Pin", { activity_id: activityId, activity_boost: activityBoost })
        this.trackedMapMarkerPinsList.push(productInfoWindow.dataset.activityId)
      }
    })

    marker.addListener("click", () => {
      // Close the infoWindow if the clicked marker is the same as the previous one
      if (this.infoBox?.position_ == marker.position) {
        this.infoBox?.close()
        this.infoBox = null
      } else {
        const infoWindowContent = productInfoWindow.children[0].cloneNode(true)

        infoWindowContent.addEventListener("click", (e) => {
          this.trackClickedActivity(e, { source: "map" })
        })

        // Close the previous infoWindow if it exists
        if (this.infoBox) this.infoBox?.close()

        // Reuse the infoWindow of the clicked marker if exists
        // Else create a new infoWindow
        if (marker.infoBox) {
          this.infoBox = marker.infoBox
          this.infoBox.open(this.map, this.marker)
        } else {
          this.infoBox = new InfoBox({
            alignBottom: true,
            pixelOffset: new google.maps.Size(-200 / 2, -22),
            infoBoxClearance: new google.maps.Size(10, 50),
            visible: true,
            closeBoxURL: "",
            zIndex: 10000,
          })
          this.infoBox.setContent(infoWindowContent)
          this.infoBox.setPosition(marker.position)
          this.infoBox.open(this.map, this.marker)
          marker.infoBox = this.infoBox
        }

        // Scroll the activity list to the corresponding activity
        // Highlight the activity in the list with a different color
        this.productListItemTargets.forEach((e) => e.children[0].classList.remove("pinned"))
        productInfoWindow.previousElementSibling.children[0].classList.add("pinned")
        productInfoWindow.previousElementSibling.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      }
    })
  }

  // Clustering functions

  initMarkerClusterer() {
    // If a clusterer already exists, remove it before creating a new one
    if (this.clusterer) {
      this.clusterer.clearMarkers();
      this.clusterer.setMap(null);
    }

    // Define how the cluster markers look like
    const renderer = {
      render: ({ count, position }) => {
        const markerContent = this.clusterMarker(count);
        const targetMarkerInfoWindows = this.productInfoWindowTargets.filter((item) => {
          const lat = parseFloat(item.dataset.lat);
          const lng = parseFloat(item.dataset.lng);
          return lat === position.lat() && lng === position.lng();
        });
        targetMarkerInfoWindows.forEach((targetMarkerInfoWindow) => {
          const targetProductListItem = targetMarkerInfoWindow.previousElementSibling;

          targetProductListItem.addEventListener("mouseover", () => {
            this.toggleClusterMarkerHovering(markerContent, true)
          })

          targetProductListItem.addEventListener("mouseout", () => {
            this.toggleClusterMarkerHovering(markerContent, false)
          })
        });

        const marker = new google.maps.marker.AdvancedMarkerElement({
          position,
          content: markerContent,
        });

        return marker;
      }
    };

    // Create the marker clusterer. It will group markers with the exact same location
    this.clusterer = new MarkerClusterer(
      {
        map: this.map,
        markers: this.markers.map(([marker]) => marker),
        renderer,
        onClusterClick: (event) => { event.preventDefault(); },
        algorithm: new ExactLocationAlgorithm({
          maxZoom: 22
        }),
      }
    );

    this.setClustersInfoWindows();
  }

  setClustersInfoWindows() {
    google.maps.event.addListener(this.clusterer, "click", (cluster) => {
      // if an infoWindow is already open and its product still present, update its content
      // else close the infoWindow
      const shouldReopenInfoBox = (!this.infoBox || this.infoBox.position_ != cluster.marker.position)

      if (this.infoBox) {
        this.infoBox.close()
        this.infoBox = null
      }

      if (shouldReopenInfoBox) {
        this.infoBox = new InfoBox({
          alignBottom: true,
          pixelOffset: new google.maps.Size(-200 / 2, -22),
          infoBoxClearance: new google.maps.Size(10, 50),
          visible: true,
          closeBoxURL: "",
          zIndex: 10000
        })

        this.infoBox.setPosition(cluster.marker.position)
        this.updateClusterInfoWindow(cluster, 0);
        this.infoBox.open(this.map)
      }
    });
  }

  updateClusterInfoWindow(cluster, markerIndex) {
    const markersInCluster = cluster.markers;
    const targetMarker = markersInCluster[markerIndex];

    const targetMarkerInfoWindow = this.markers.find(([marker]) => marker == targetMarker)[1];
    const infoWindowContent = targetMarkerInfoWindow.children[0].cloneNode(true)

    const navigationContainer = document.createElement("div");
    navigationContainer.classList.add("d-flex", "justify-content-between", "align-items-center");

    const previousButton = document.createElement("button");
    previousButton.classList.add("btn", "btn-sm", "btn-outline-primary", "btn-icon__square-sm");
    previousButton.innerHTML = '<i class="fa-arrow-left fas"></i>';

    const nextButton = document.createElement("button");
    nextButton.classList.add("btn", "btn-sm", "btn-outline-primary", "btn-icon__square-sm");
    nextButton.innerHTML = '<i class="fa-arrow-right fas"></i>';

    const counterSpan = document.createElement("span");
    counterSpan.classList.add("font-size-sm", "text-muted");
    counterSpan.textContent = `${markerIndex + 1}/${cluster.markers.length}`;

    navigationContainer.appendChild(previousButton);
    navigationContainer.appendChild(counterSpan);
    navigationContainer.appendChild(nextButton);

    previousButton.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const newIndex = (markerIndex - 1 + markersInCluster.length) % markersInCluster.length;
      this.updateClusterInfoWindow(cluster, newIndex);
    });

    nextButton.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const newIndex = (markerIndex + 1) % markersInCluster.length;
      this.updateClusterInfoWindow(cluster, newIndex);
    });

    infoWindowContent.children[0].appendChild(navigationContainer);
    this.infoBox.setContent(infoWindowContent);


    // Scroll the activity list to the corresponding activity
    // Highlight the activity in the list with a different color
    this.productListItemTargets.forEach((e) => e.children[0].classList.remove("pinned"))
    targetMarkerInfoWindow.previousElementSibling.children[0].classList.add("pinned")
    targetMarkerInfoWindow.previousElementSibling.scrollIntoView({
      behavior: "smooth",
      block: "center",
    })

    // Track the activity pin if it's not already tracked
    const { activityId, activityBoost } = targetMarkerInfoWindow.dataset
    if (this.trackedMapMarkerPinsList.indexOf(activityId) == -1) {
      analytics.track("Activity Search Map Pin", { activity_id: activityId, activity_boost: activityBoost })
      this.trackedMapMarkerPinsList.push(activityId)
    }
  }

  clusterMarker(count) {
    const container = document.createElement("div");

    container.style.display = "flex";
    container.style.alignItems = "center";
    container.style.justifyContent = "center";
    container.style.position = "relative";
    container.style.width = "24px";
    container.style.height = "24px";
    container.style.transform = "translateY(50%)";

    container.innerHTML = this.clusterMarkerIcon(count);

    container.addEventListener("mouseenter", () => {
      this.toggleClusterMarkerHovering(container, true)
    })

    container.addEventListener("mouseleave", () => {
      this.toggleClusterMarkerHovering(container, false)
    })

    return container;
  }

  clusterMarkerIcon(count, color = PIN_COLORS["default"], radius = 12, fontSize = "12px") {
    return `
      <svg
        width="${radius * 2}" height="${radius * 2}" viewBox="0 0 ${radius * 2} ${radius * 2}" fill="none" xmlns="http://www.w3.org/2000/svg"
        style="position: absolute; filter: drop-shadow(0px 3px 4px rgba(0, 0, 0, 0.4));"
      >
        <circle cx="${radius}" cy="${radius}" r="${radius}" fill="white"/>
      </svg>

      <span style="
        position: relative;
        font-family: sans-serif;
        font-size: ${fontSize};
        font-weight: bold;
        color: ${color};
        z-index: 1;
      ">
        ${count}
      </span>
    `;
  }

  toggleClusterMarkerHovering(marker, isHovered) {
    if (isHovered) {
      marker.children[1].style.color = PIN_COLORS["hover"]
      marker.children[1].style.fontSize = "14px"
      marker.children[0].style.width = 28
      marker.children[0].style.height = 28
      marker.zIndex = 3
    } else {
      marker.children[1].style.color = PIN_COLORS["default"]
      marker.children[1].style.fontSize = "12px"
      marker.children[0].style.width = 24
      marker.children[0].style.height = 24
      marker.zIndex = 1
    }
  }

  // Tracking Methods
  trackClickedActivity(event, params = {}) {
    const isArrowClicked = event.target.closest(".swiper .control")
    const isButtonClicked = event.target.closest("button")

    if (isArrowClicked || isButtonClicked) {
      return
    }

    const element = event.currentTarget.querySelector(
      ".c--activity-list-item, .c--activity--info-window"
    )
    const { activityId, position, page, activityBoost } = element.dataset
    const totalPosition = this.productPosition(position, page)
    const properties = {
      activity_id: activityId,
      activity_boost: activityBoost,
      position: totalPosition,
      source: "list",
      ...params,
    }
    analytics.track("Search Result Clicked", properties)
  }

  trackVisibleActivities() {
    this.productListItemTargets.forEach((card) => {
      const { activityId, position, page, activityBoost } = card.children[0].dataset
      if(
        this.isScrolledIntoView &&
        this.isScrolledIntoView(card) &&
        this.trackedProductList.indexOf(activityId) == -1
      ) {
        const totalPosition = this.productPosition(position, page)
        const properties = {
          activity_id: activityId,
          activity_boost: activityBoost,
          position: totalPosition,
          page,
          source: "listing",
        }
        analytics.track("Viewed Search Result", properties)
        this.trackedProductList.push(activityId)
      }
    })
  }

  productPosition(position, page) {
    if (!position || !page) return
    return (page - 1) * PRODUCT_PER_PAGE + (Number(position) + 1)
  }
}
